import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProjects } from "../hooks/useProjects";
import * as api from "../services/projectApi";
import { useAuth } from "../../auth/AuthContext";

const STATUS_COLORS = {
  draft: "var(--text3)",
  active: "var(--green)",
  completed: "var(--accent)",
  archived: "var(--amber)",
};

export default function ProjectList() {
  const navigate = useNavigate();
  const { has } = useAuth();
  const { items, loading, error, params, setParams, refetch } = useProjects();
  const [search, setSearch] = useState("");

  const applySearch = (e) => {
    e.preventDefault();
    setParams((p) => ({ ...p, search: search || undefined, page: 1 }));
  };

  const act = async (fn, id) => {
    try {
      await fn(id);
      refetch();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  return (
    <div className="proj-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="proj-muted">AI-planned marketing projects</p>
        </div>
        {has("project.create") && (
          <button className="proj-btn proj-btn-primary" onClick={() => navigate("/projects/new")}>
            + New Project
          </button>
        )}
      </div>

      <div className="proj-body">
        <div className="proj-toolbar">
          <form onSubmit={applySearch} className="proj-search">
            <input
              placeholder="Search projects…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="proj-btn" type="submit">
              Search
            </button>
          </form>
          <select
            className="proj-select"
            value={params.status || ""}
            onChange={(e) => setParams((p) => ({ ...p, status: e.target.value || undefined, page: 1 }))}
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {loading && <div className="proj-center proj-muted">Loading projects…</div>}
        {error && <div className="proj-alert">{error}</div>}

        {!loading && !error && items.length === 0 && (
          <div className="proj-empty">
            <p>No projects yet.</p>
            {has("project.create") && (
              <button className="proj-btn proj-btn-primary" onClick={() => navigate("/projects/new")}>
                Create your first project
              </button>
            )}
          </div>
        )}

        <div className="proj-grid">
          {items.map((p) => (
            <div key={p.id} className="proj-card" onClick={() => navigate(`/projects/${p.id}`)}>
              <div className="proj-card-top">
                <span
                  className="proj-badge"
                  style={{ color: STATUS_COLORS[p.status], borderColor: STATUS_COLORS[p.status] }}
                >
                  {p.status}
                </span>
                {p.platforms?.map((pl) => (
                  <span key={pl.id} className="proj-chip">
                    {pl.platform}
                  </span>
                ))}
              </div>
              <h3>{p.projectName}</h3>
              <p className="proj-muted">{p.businessName}</p>
              {p.industry && <p className="proj-sub">{p.industry}</p>}
              {p.owner && <p className="proj-sub">Owner: {p.owner.name}</p>}
              <div className="proj-card-stats">
                <span>{p._count?.kpis || 0} KPIs</span>
                <span>{p._count?.notes || 0} notes</span>
              </div>
              <div className="proj-card-actions" onClick={(e) => e.stopPropagation()}>
                <button className="proj-link" onClick={() => navigate(`/projects/${p.id}`)}>
                  Open
                </button>
                {has("project.update") && (
                  <button className="proj-link" onClick={() => navigate(`/projects/${p.id}/wizard`)}>
                    Edit
                  </button>
                )}
                {has("project.create") && (
                  <button className="proj-link" onClick={() => act(api.duplicateProject, p.id)}>
                    Duplicate
                  </button>
                )}
                {has("project.archive") && p.status !== "archived" && (
                  <button className="proj-link" onClick={() => act(api.archiveProject, p.id)}>
                    Archive
                  </button>
                )}
                {has("project.delete") && (
                  <button
                    className="proj-link proj-link-danger"
                    onClick={() => {
                      if (window.confirm(`Delete "${p.projectName}"?`)) act(api.deleteProject, p.id);
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
